import { useEffect, useMemo, useState } from "react";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api, { getPublicFileUrl, formatApiErrorDetail } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { getEffectivePricingDisplay, getPriceBadge, SERVICE_KIND_BADGE_LABELS } from "@/lib/pricingUtils";
import PriceBadge from "@/components/PriceBadge";
import { PUBLIC_BOOKING } from "@/constants/testIds";

const FALLBACK_IMAGES = [
  "https://images.pexels.com/photos/30809947/pexels-photo-30809947.jpeg",
  "https://images.pexels.com/photos/4586711/pexels-photo-4586711.jpeg",
];

export default function StepServices({ clinic, onSelect }) {
  const { t } = usePublicLang();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/public/clinics/${clinic.slug}/services`)
      .then(({ data }) => { if (active) setServices(data); })
      .catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [clinic.slug]);

  const featuredServices = useMemo(
    () => services.filter((s) => s.service_kind === "consultation" || s.service_kind === "review" || s.service_kind === "model"),
    [services]
  );
  const catalogServices = useMemo(
    () => services.filter((s) => s.service_kind !== "consultation" && s.service_kind !== "review" && s.service_kind !== "model"),
    [services]
  );

  const categories = useMemo(() => {
    const set = new Set(catalogServices.map((s) => s.category).filter(Boolean));
    return Array.from(set);
  }, [catalogServices]);

  const visibleServices = activeCategory === "all" ? catalogServices : catalogServices.filter((s) => s.category === activeCategory);
  const groupedByCategory = useMemo(() => {
    if (categories.length === 0) return [{ category: null, items: visibleServices }];
    const groups = {};
    const noCategory = [];
    visibleServices.forEach((s) => {
      if (!s.category) { noCategory.push(s); return; }
      groups[s.category] = groups[s.category] || [];
      groups[s.category].push(s);
    });
    const ordered = categories.filter((c) => groups[c]).map((c) => ({ category: c, items: groups[c] }));
    if (noCategory.length > 0) ordered.push({ category: null, items: noCategory });
    return ordered;
  }, [visibleServices, categories]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }
  if (error) {
    return <p className="text-center text-[#9E4747] py-8">{error}</p>;
  }
  if (services.length === 0) {
    return (
      <p className="text-center text-[#5C554D] py-12" data-testid={PUBLIC_BOOKING.servicesEmpty}>
        {t("step1.empty")}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {featuredServices.length > 0 && (
        <div className="space-y-2.5 bg-[var(--brand-secondary)]/50 border border-[var(--brand-primary)]/25 rounded-2xl p-4 sm:p-5" data-testid={PUBLIC_BOOKING.featuredServicesSection}>
          <p className="text-sm font-semibold text-[var(--brand-primary)]">{t("step1.featuredTitle")}</p>
          <div className="space-y-2">
            {featuredServices.map((s) => {
              const pricingDisplay = getEffectivePricingDisplay(s);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSelect(s)}
                  className="w-full flex items-center justify-between gap-3 bg-white rounded-xl px-4 py-3 border border-[rgba(140,125,112,0.2)] hover:border-[var(--brand-primary)] transition-colors text-left active:scale-[0.99]"
                  data-testid={`${PUBLIC_BOOKING.featuredServiceCardPrefix}${s.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm sm:text-base">
                      {s.service_kind === "consultation" ? t("step1.featuredConsultationCopy") : s.service_kind === "review" ? t("step1.featuredReviewCopy") : t("step1.featuredModelCopy")}
                    </p>
                    <p className="text-xs text-[#5C554D] mt-0.5">{s.name}{s.show_duration && ` · ${s.duration_minutes} ${t("common.minutesShort")}`}</p>
                  </div>
                  <span className="text-sm font-semibold text-[var(--brand-primary)] shrink-0">{pricingDisplay.regularText}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {catalogServices.length === 0 ? null : (
        <>
      {categories.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            type="button"
            onClick={() => setActiveCategory("all")}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeCategory === "all" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#5C554D] border-[rgba(140,125,112,0.25)]"}`}
            data-testid={`${PUBLIC_BOOKING.categoryTabPrefix}all`}
          >
            {t("step1.allCategories")}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActiveCategory(c)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeCategory === c ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#5C554D] border-[rgba(140,125,112,0.25)]"}`}
              data-testid={`${PUBLIC_BOOKING.categoryTabPrefix}${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-6" data-testid={PUBLIC_BOOKING.servicesList}>
        {groupedByCategory.map((group) => (
          <div key={group.category || "sin-categoria"} className="space-y-3">
            {group.category && categories.length > 1 && activeCategory === "all" && (
              <h4 className="text-sm font-semibold text-[#5C554D] tracking-wide uppercase" data-testid={`${PUBLIC_BOOKING.categoryHeadingPrefix}${group.category}`}>
                {group.category}
              </h4>
            )}
            {group.items.map((s, idx) => {
              const expanded = expandedId === s.id;
              const pricingDisplay = getEffectivePricingDisplay(s);
              const kindBadge = SERVICE_KIND_BADGE_LABELS[s.service_kind];
              return (
                <div
                  key={s.id}
                  className="w-full bg-white border border-[rgba(140,125,112,0.2)] rounded-2xl p-3 sm:p-4 hover:shadow-md hover:border-[var(--brand-primary)] transition-all"
                  data-testid={`${PUBLIC_BOOKING.serviceCardPrefix}${s.id}`}
                >
                  <button type="button" onClick={() => onSelect(s)} className="w-full flex items-center gap-4 text-left active:scale-[0.99] transition-transform">
                    <img
                      src={s.photo_url ? getPublicFileUrl(s.photo_url) : FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length]}
                      alt={s.name}
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-base sm:text-lg leading-snug flex items-center gap-2">
                        {s.name}
                        {kindBadge && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wide text-[var(--brand-primary)] bg-[var(--brand-secondary)] px-2 py-0.5 rounded-full shrink-0"
                            data-testid={`${PUBLIC_BOOKING.kindBadgePrefix}${s.id}`}
                          >
                            {kindBadge}
                          </span>
                        )}
                      </h3>
                      {s.description && <p className="text-sm text-[#5C554D] mt-0.5 line-clamp-2">{s.description}</p>}
                      <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
                        {s.show_duration && <span className="text-[#5C554D]">{s.duration_minutes} {t("common.minutesShort")}</span>}
                        {/* Fix AT (2026-10-27): el precio va en el sello redondo de la derecha; aca queda el precio normal tachado (si hay promocion) y la aclaracion */}
                        {pricingDisplay.isPromoActive && <span className="line-through text-[#9C948A]">{pricingDisplay.regularText}</span>}
                        {getPriceBadge(s).note && <span className="text-[#5C554D]">{getPriceBadge(s).note}</span>}
                      </div>
                      {pricingDisplay.isPromoActive && pricingDisplay.promoLabel && (
                        <span
                          className="inline-block mt-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"
                          data-testid={`${PUBLIC_BOOKING.promoLabelBadgePrefix}${s.id}`}
                        >
                          {pricingDisplay.promoLabel}
                        </span>
                      )}
                    </div>
                    <PriceBadge service={s} size="sm" className="shrink-0" testId={`${PUBLIC_BOOKING.servicePricePrefix}${s.id}`} />
                  </button>
                  {s.description && (
                    <>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : s.id)}
                        className="flex items-center gap-1 text-xs font-medium text-[var(--brand-primary)] mt-2"
                        data-testid={`${PUBLIC_BOOKING.readMoreButtonPrefix}${s.id}`}
                      >
                        {expanded ? t("step1.readLess") : t("step1.readMore")}
                        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {expanded && (
                        <p className="text-sm text-[#5C554D] whitespace-pre-line mt-2 leading-relaxed">{s.description}</p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
}
